<template>
  <app-page class="dev-page" fill lean>
    <!-- Kopf wie Verkäufe, Zahlungen und Saldo (Kundenwunsch 13.10.2026): leichter Titel, rechts die Aktionen mit Icon
         und Beschriftung (Handy: Icon-Knöpfe) -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
      <app-button icon="plus" @click="addDevice">Gerät anlegen</app-button>
    </template>
    <!-- Werkzeugleiste: links die Suche, rechts der Filter (Panel wie Verkäufe statt der Filterzeile; „Aktiv“ wie
         bisher vorbelegt) -->
    <template #toolbar>
      <app-search v-model="searchText" placeholder="In Geräten suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :active-filters="activeFilterCount"
        :result-nouns="resultNouns"
        :empty-value="emptyDeviceFilters"
        :show-range="false"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <div class="dev-gridcol">
      <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe); ein Klick öffnet rechts das Gerät zum
           Bearbeiten (Kundenwunsch 13.10.2026: wie Benutzer, nicht mehr in der Tabelle) -->
      <div class="app-grid-fill dev-grid" :class="{ 'dev-grid--auto': handy }">
        <dx-data-grid
          id="gridContainer"
          :ref="gridRefName"
          :height="gridHeight"
          :data-source="devicesDataSource"
          :columns="columns"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          :focused-row-enabled="true"
          :on-data-error-occurred="onDataErrorOccurred"
          @toolbar-preparing="onToolbarPreparing"
          @focused-row-changing="onFocusedRowChanging"
          @focused-row-changed="onFocusedRowChanged"
          @row-prepared="onRowPrepared"
          @content-ready="onContentReady"
        >
          <dx-paging :page-size="defaultPageSize" />
          <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
          <dx-pager :visible="false" />
          <dx-search-panel :visible="false" />
          <dx-selection mode="none" />
          <template #druckerZelle="{ data }">
            <span class="dev-printer">{{ data.value }}</span>
          </template>
          <!-- Status deutlich (Kundenwunsch 13.10.2026): Punkt mit „Aktiv“/„Inaktiv“, dahinter der Tag „Test“ -->
          <template #statusZelle="{ data }">
            <span class="dev-status">
              <span
                class="dev-status__dot"
                :class="isOne(data.data.active) ? 'dev-status__dot--on' : 'dev-status__dot--off'"
                aria-hidden="true"
              ></span>
              <span class="dev-status__text">{{ isOne(data.data.active) ? "Aktiv" : "Inaktiv" }}</span>
              <app-tag v-if="isOne(data.data.is_test_device)" kind="test" />
            </span>
          </template>
          <!-- Letzte Meldungen (Kundenwunsch 27.10.2026): relative Zeit, genaue im Tooltip; nur der Upload mit Punkt nach
               Alter (grün unter 24 Stunden, gelb bis 7 Tage, grau älter) – kein „online“ -->
          <template #meldungZelle="{ data }">
            <span class="dev-time" :title="exactText(data.value)">
              <span
                v-if="data.column.dataField === 'last_upload'"
                class="dev-age"
                :class="'dev-age--' + ageOf(data.value)"
                aria-hidden="true"
              ></span>{{ relativeText(data.value) }}
            </span>
          </template>
          <template #mutedZelle="{ data }">
            <span class="dev-muted">{{ data.value || "–" }}</span>
          </template>
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
        </template>
      </app-grid-footer>

      <!-- Dialoge liegen über der Seite und nehmen keinen Platz im Inhalt ein -->
      <app-dialog v-model="dialog" title="Letzter bekannter Standort" :max-width="512" nested blur>
        <div class="device-map">
          <l-map :zoom="13" style="height: 512px; width: 100%" :center="centerComputed">
            <!--  url="http://map{s}.defas-fgi.de/OrthoMRCV/Tiles/zoomlevel{z}/columns{colMin}-{colMax}/column{x}/rows{rowMin}-{rowMax}/{y}_{x}_{z}.jpg"  -->
            <l-tile-layer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="Map data  &copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors"
            />
            <l-marker :lat-lng="lastLocation" class="leaflet-icon" :icon="icon">
              <l-tooltip :content="tooltip"></l-tooltip>
            </l-marker>
          </l-map>
          <app-state-overlay v-if="loading" state="loading" :offset-top="0" />
        </div>
        <template #actions>
          <app-button @click="dialog = false">Schließen</app-button>
        </template>
      </app-dialog>

      <app-dialog :value="discardDialog" title="Änderungen verwerfen?" @input="onDiscardDialogInput">
        <p class="dev-dialog__text">Die ungespeicherten Änderungen gehen verloren.</p>
        <template #actions>
          <app-button @click="keepEditing">Weiter bearbeiten</app-button>
          <app-button kind="danger" @click="confirmDiscard">Verwerfen</app-button>
        </template>
      </app-dialog>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!editMode"
        icon="tablet"
        :title="editMode === 'create' ? 'Gerät anlegen' : 'Gerät bearbeiten'"
        close-label="Bearbeiten schließen"
        :dirty="dirty"
        @close="onDetailClose"
        @layout-change="onLayoutChange"
      >
        <template #subtitle>
          <app-tag v-if="editMode === 'edit' && editRow && !isOne(editRow.active)" kind="off" />
          <app-tag v-if="editMode === 'edit' && editRow && isOne(editRow.is_test_device)" kind="test" />
        </template>
        <device-editor
          v-if="editMode"
          :value="draft"
          :device="editRow"
          :mode="editMode"
          :providers="paymentProviderOptions"
          :errors="errors"
          @field="onField"
          @show-location="showLastLocation(editRow.printer_id)"
        />
        <!-- Fuß wie bei Benutzer: „1 Änderung“ bzw. ein Fehler nur bei Bedarf, darunter Verwerfen und Speichern -->
        <template #footer>
          <span class="app-sr-only" role="status">{{ saveError || changeText }}</span>
          <span
            v-if="saveError || dirty"
            class="dev-foot__text"
            :class="{ 'dev-foot__text--error': !!saveError }"
            aria-hidden="true"
          >
            <app-icon v-if="saveError" name="alert" :size="14" />{{ saveError || changeText }}
          </span>
          <app-button :disabled="!dirty || saving" @click="revertChanges">Verwerfen</app-button>
          <app-button kind="primary" icon="check" :disabled="!dirty" :loading="saving" @click="saveChanges">
            Speichern
          </app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
// import { latLng } from "leaflet";
import L from "leaflet";
import moment from "moment";
import { LMap, LTileLayer, LTooltip, LMarker } from "vue2-leaflet";
import {
  DxDataGrid,
  DxPaging,
  DxSearchPanel,
  DxPager,
  DxSelection,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import DataGridMixin from "@/mixins/DataGridMixin";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import DeviceEditor from "@/components/masterdata/DeviceEditor.vue";
import { emptyFilters, activeCount, buildPredicate, buildFacets, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { DEVICE_FILTER_CONFIG, UPLOAD_AGE_TEXT } from "@/components/masterdata/masterFilters";
import { relativeTime, exactTime, ageLevel } from "@/utils/relativeTime";
import { exportWithColumns } from "@/utils/gridExport";
import { formatNumber, plural } from "@/utils/format";

// Felder, die der Detailbereich speichert (wie bisher die bearbeitbaren Spalten der Tabelle)
const EDIT_FIELDS = ["device_id", "comment", "active", "payment_provider", "is_test_device"];
const CREATE_FIELDS = ["printer_id", "device_id", "comment", "active", "payment_provider", "is_test_device"];
// Schalter: im Entwurf Boolean, zur API wie bisher setCellValue als 1/0
const BOOLEAN_FIELDS = ["active", "is_test_device"];
// Weicht eine dieser Spalten nach dem Neuladen ab, übernimmt der Detailbereich (ohne offene Änderungen) den neuen Stand
const SYNC_FIELDS = EDIT_FIELDS.concat(["app_version", "last_upload", "last_gps", "last_app_start"]);

function isOne(value) {
  return value === 1 || value === "1" || value === true;
}

function textValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

// null und "" gelten als gleich, Schalter als Boolean
function sameValue(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") {
    return !!a === !!b;
  }
  return textValue(a) === textValue(b);
}

function emptyDraft() {
  return {
    printer_id: "",
    device_id: "",
    comment: "",
    payment_provider: null,
    active: true,
    is_test_device: false,
  };
}

export default {
  components: {
    // Devexpress
    DxDataGrid,
    DxPaging,
    DxSearchPanel,
    DxPager,
    DxSelection,
    // Leaflet
    LMap,
    LTileLayer,
    LTooltip,
    LMarker,
    SaleFilterMenu,
    DeviceEditor,
  },
  mixins: [DataGridMixin],

  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach Filter und Suche, „4 von 24 Geräten“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Gerät", "Geräten");
    },
    summaryNoun() {
      return plural(this.summary.count, "Gerät", "Geräte");
    },
    activeFilterCount() {
      return activeCount(this.filters, DEVICE_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, DEVICE_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    dataUrl() {
      return `${this.$store.state.baseUrl}device`;
    },
    centerComputed() {
      return this.lastLocation && this.lastLocation.lat && this.lastLocation.lng
        ? this.lastLocation
        : this.center;
    },
    icon() {
      return new L.DivIcon({
        className: "leaflet-icon-container",
        html: `<div class='leaflet-icon'><div class='center-abs'>${this.printer_id}</div></div>`,
        iconSize: [50, 50],
      });
    },
    changedFields() {
      if (!this.editMode) {
        return [];
      }
      const fields = this.editMode === "create" ? CREATE_FIELDS : EDIT_FIELDS;
      return fields.filter((key) => !sameValue(this.draft[key], this.original[key]));
    },
    dirty() {
      return this.changedFields.length > 0;
    },
    changeText() {
      const n = this.changedFields.length;
      if (!n) {
        return "Keine Änderungen";
      }
      return n === 1 ? "1 Änderung" : n + " Änderungen";
    },
  },
  data() {
    return {
      loading: false,
      center: { lat: 48.9853399, lng: 12.9804694 },
      lastLocation: { lat: null, lng: null },

      gridRefName: "dataGrid",
      // Zeiger auf den Zeilen: ein Klick öffnet den Detailbereich
      gridAttr: { class: "app-grid--detail" },
      searchText: "",
      dialog: false,
      printer_id: "",
      // Inhalt des Tooltips am Standort im Dialog (vorher nicht deklariert: Vue-Warnung beim Zeichnen)
      tooltip: "",
      // Filter-Panel (Kundenwunsch 13.10.2026): Zustand, Werte der Listen, Treffer nach Filter und Suche
      // Bezug der relativen Zeiten in den Meldungsspalten
      now: new Date(),
      filters: emptyFilters(DEVICE_FILTER_CONFIG),
      filterSegments: DEVICE_FILTER_CONFIG.segments,
      resultNouns: ["Gerät", "Geräte"],
      facets: [],
      resultCount: null,
      summary: { count: 0, loaded: 0 },
      paymentProviderOptions: [
        { value: "SUMUP", text: "SUMUP" },
        { value: "STRIPE", text: "STRIPE" },
        { value: "VIVA", text: "VIVA" },
      ],
      // Detailbereich: null | 'edit' | 'create'
      editMode: null,
      // gespeicherter Stand der bearbeiteten Zeile (eingefroren)
      editRow: null,
      draft: emptyDraft(),
      original: emptyDraft(),
      errors: {},
      saving: false,
      saveError: "",
      discardDialog: false,
      // Definitionsreihenfolge = Excel-Spalten wie bisher (exportWithColumns ohne „Status“); Bildschirm: Drucker ID,
      // Geräte ID, Status, Zahlungsanbieter, App Version, Letzter Verkauf, Letztes GPS Signal, Bemerkung
      columns: [
        {
          dataField: "printer_id",
          caption: "Drucker ID",
          alignment: "left",
          sortOrder: "asc",
          width: 124,
          visibleIndex: 0,
          cellTemplate: "druckerZelle",
        },
        {
          dataField: "device_id",
          caption: "Geräte ID",
          alignment: "left",
          minWidth: 130,
          visibleIndex: 1,
        },
        {
          dataField: "comment",
          caption: "Bemerkung",
          alignment: "left",
          minWidth: 120,
          // zuletzt, nach den drei Meldungsspalten (seit 27.10.2026)
          visibleIndex: 8,
          cellTemplate: "mutedZelle",
        },
        {
          dataField: "active",
          caption: "Aktiv",
          dataType: "boolean",
          visible: false,
        },
        {
          dataField: "payment_provider",
          caption: "Zahlungsanbieter",
          alignment: "left",
          width: 140,
          visibleIndex: 3,
          lookup: {
            dataSource: [
              { value: "SUMUP", text: "SUMUP" },
              { value: "STRIPE", text: "STRIPE" },
              { value: "VIVA", text: "VIVA" },
            ],
            displayExpr: "text",
            valueExpr: "value",
          },
        },
        {
          dataField: "is_test_device",
          caption: "Testgerät",
          dataType: "boolean",
          visible: false,
        },
        {
          dataField: "app_version",
          caption: "App Version",
          alignment: "left",
          width: 112,
          visibleIndex: 4,
        },
        // Letzte Meldungen des Geräts (Kundenwunsch 27.10.2026, vorher „Letzter Verkauf“ und „Letztes GPS Signal“, die
        // die Schnittstelle leer lieferte): Upload = wann der letzte Verkauf auf dem Server ankam, GPS-Signal und
        // App-Start einzeln und klar benannt, ohne daraus „online“ abzuleiten
        {
          dataField: "last_upload",
          caption: "Letzter Upload",
          alignment: "left",
          dataType: "datetime",
          format: "dd.MM.yyyy HH:mm:ss",
          width: 140,
          visibleIndex: 5,
          cellTemplate: "meldungZelle",
        },
        {
          dataField: "last_gps",
          caption: "Letztes GPS-Signal",
          alignment: "left",
          dataType: "datetime",
          format: "dd.MM.yyyy HH:mm:ss",
          width: 140,
          visibleIndex: 6,
          cellTemplate: "meldungZelle",
        },
        {
          dataField: "last_app_start",
          caption: "Letzter App-Start",
          alignment: "left",
          dataType: "datetime",
          format: "dd.MM.yyyy HH:mm:ss",
          width: 140,
          visibleIndex: 7,
          cellTemplate: "meldungZelle",
        },
        {
          // nur Bildschirm: Aktiv/Inaktiv und Test auf einen Blick (Kundenwunsch 13.10.2026)
          name: "status",
          caption: "Status",
          width: 140,
          visibleIndex: 2,
          cellTemplate: "statusZelle",
          calculateCellValue: (row) => (isOne(row.active) ? "Aktiv" : "Inaktiv") + (isOne(row.is_test_device) ? " Test" : ""),
        },
      ],
      devicesDataSource: new CustomStore({
        key: ["customer_id", "printer_id", "device_id"],
        load: () =>
          axios.get(`${this.dataUrl}?customer_id=${this.$store.state.customerId}`).then((response) => {
            this._rows = Array.isArray(response.data) ? response.data : [];
            // Bezug der relativen Zeiten („vor 4 Min.“) ist der Zeitpunkt des Ladens
            this.now = new Date();
            this.refreshFacets();
            return response;
          }),
        insert: (newDevice) =>
          axios.post(
            `${this.dataUrl}?customer_id=${this.$store.state.customerId}`,
            newDevice
          ),
        update: (deviceKeys, deviceUpdates) =>
          axios.put(
            `${this.dataUrl}?customer_id=${deviceKeys.customer_id}&device_id=${deviceKeys.device_id}`,
            deviceUpdates
          ),
        remove: (deviceKeys) =>
          axios.delete(
            `${this.dataUrl}?customer_id=${deviceKeys.customer_id}&device_id=${deviceKeys.device_id}`
          ),
      }),
    };
  },
  created() {
    // nicht reaktiv: geladene Zeilen und Fortsetzungen der Rückfrage
    this._rows = null;
    this.pendingDiscard = null;
    this.pendingKeep = null;
  },
  mounted() {
    // Vorbelegung „Aktiv“ wie bisher die Filterzeile
    this.onFiltersInput(this.filters);
  },
  beforeRouteLeave(to, from, next) {
    // Abmelden und laufendes Speichern nicht aufhalten
    if (!this.dirty || this.saving || !this.$store.state.isLoggedIn) {
      next();
      return;
    }
    this.askDiscard(
      () => next(),
      () => next(false)
    );
  },
  methods: {
    isOne,
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance ? ref.instance : null;
    },
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptyDeviceFilters() {
      return emptyFilters(DEVICE_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (listKey === "upload") {
        return UPLOAD_AGE_TEXT[key] || key;
      }
      return key === "" ? NO_VALUE_TEXT : key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, DEVICE_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptyDeviceFilters();
      const grid = this.grid();
      if (grid) {
        const match = buildPredicate(this.filters, DEVICE_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
      this.refreshFacets();
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Number(grid.totalCount());
      this.resultCount = count >= 0 ? count : 0;
      this.summary = { count: this.resultCount, loaded: (this._rows || []).length };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
      this.syncEditRow();
    },
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst steht ihre Unterkante als zweite
      // Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    // inaktive Geräte blasser (Kundenwunsch 13.10.2026: „deutlicher hervorheben ob aktiv“)
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.data) {
        return;
      }
      const el = e.rowElement && e.rowElement.get ? e.rowElement.get(0) : e.rowElement;
      if (el && el.classList) {
        el.classList.toggle("dev-row--off", !isOne(e.data.active));
      }
    },
    onSearchInput() {
      this.grid().searchByText(this.searchText || "");
    },
    relativeText(value) {
      return relativeTime(value, this.now) || "–";
    },
    exactText(value) {
      return exactTime(value);
    },
    ageOf(value) {
      return ageLevel(value, this.now);
    },
    refreshDataGrid() {
      this.grid().refresh();
    },
    exportToExcel() {
      // wie bisher die neun Datenspalten in Definitionsreihenfolge mit dem aktuellen Filter; ohne „Status“
      exportWithColumns(this.grid(), { exclude: ["status"] });
    },
    onDataErrorOccurred(e) {
      // Speichern läuft nicht mehr über das Grid; hier landen nur Ladefehler
      if (e && e.error) {
        e.error.message = "Geräte konnten nicht geladen werden.";
      }
    },

    // ---------------------------------------------------------------- Auswahl und Detailbereich
    onFocusedRowChanging(e) {
      if (this.saving) {
        e.cancel = true;
        return;
      }
      if (!this.dirty || e.newRowIndex === e.prevRowIndex) {
        return;
      }
      e.cancel = true;
      const index = e.newRowIndex;
      this.askDiscard(() => {
        const grid = this.grid();
        if (grid) {
          grid.option("focusedRowIndex", index);
        }
      });
    },
    onFocusedRowChanged(e) {
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      if (this.isEditRow(row.data)) {
        return;
      }
      this.openEdit(row.data);
    },
    isEditRow(data) {
      return this.editMode === "edit" && !!this.editRow && !!data && String(data.printer_id) === String(this.editRow.printer_id);
    },
    findDevice(printerId) {
      const rows = Array.isArray(this._rows) ? this._rows : [];
      return rows.find((d) => d && String(d.printer_id) === String(printerId)) || null;
    },
    // Entwurf beim Öffnen aus der Zeile kopieren (active, is_test_device als Boolean für die Schalter)
    openEdit(row) {
      const saved = Object.freeze(Object.assign({}, row));
      const draft = {
        printer_id: textValue(saved.printer_id),
        device_id: textValue(saved.device_id),
        comment: textValue(saved.comment),
        payment_provider: saved.payment_provider || null,
        active: isOne(saved.active),
        is_test_device: isOne(saved.is_test_device),
      };
      this.editRow = saved;
      this.draft = draft;
      this.original = Object.assign({}, draft);
      this.errors = {};
      this.saveError = "";
      this.editMode = "edit";
    },
    // nach dem Neuladen den Detailbereich ohne offene Änderungen auffrischen
    syncEditRow() {
      if (this.editMode !== "edit" || !this.editRow || this.dirty || this.saving) {
        return;
      }
      const fresh = this.findDevice(this.editRow.printer_id);
      if (fresh && SYNC_FIELDS.some((key) => fresh[key] !== this.editRow[key])) {
        this.openEdit(fresh);
      }
    },
    openCreate() {
      const draft = emptyDraft();
      this.editRow = null;
      this.draft = draft;
      this.original = Object.assign({}, draft);
      this.errors = {};
      this.saveError = "";
      this.editMode = "create";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    addDevice() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.openCreate());
        return;
      }
      this.openCreate();
    },
    closeDetail() {
      this.editMode = null;
      this.editRow = null;
      this.draft = emptyDraft();
      this.original = emptyDraft();
      this.errors = {};
      this.saveError = "";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailClose() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.closeDetail());
        return;
      }
      this.closeDetail();
    },
    // offener Detailbereich: Bemerkung, App-Version und GPS ausblenden (stehen im Detail), sonst wird die Tabelle zu schmal
    onLayoutChange(e) {
      const grid = this.grid();
      if (grid) {
        const open = !!(e && e.open);
        grid.beginUpdate();
        grid.columnOption("comment", "visible", !open);
        grid.columnOption("last_gps", "visible", !open);
        grid.columnOption("last_app_start", "visible", !open);
        grid.columnOption("app_version", "visible", !open);
        grid.endUpdate();
      }
      this.$nextTick(() => {
        const current = this.grid();
        if (current) {
          current.updateDimensions();
        }
      });
    },
    onField(key, value) {
      this.draft[key] = value;
      if (this.errors[key]) {
        const errors = Object.assign({}, this.errors);
        delete errors[key];
        this.errors = errors;
      }
      this.saveError = "";
    },
    revertChanges() {
      this.draft = Object.assign({}, this.original);
      this.errors = {};
      this.saveError = "";
    },
    validateCreate() {
      const errors = {};
      const id = textValue(this.draft.printer_id).trim();
      if (!id) {
        errors.printer_id = "Pflichtfeld";
      } else if (!/^\d+$/.test(id)) {
        errors.printer_id = "Bitte eine ganze Zahl eingeben.";
      } else if (this.findDevice(id)) {
        errors.printer_id = "Drucker ID bereits vergeben.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    // Speichern über dieselben Store-Funktionen wie bisher das Bearbeiten in der Tabelle (PUT bzw. POST device)
    async saveChanges() {
      if (!this.dirty || this.saving) {
        return;
      }
      const create = this.editMode === "create";
      if (create && !this.validateCreate()) {
        return;
      }
      const draft = Object.assign({}, this.draft);
      const row = this.editRow;
      const value = (key) => (BOOLEAN_FIELDS.indexOf(key) !== -1 ? (draft[key] ? 1 : 0) : draft[key]);
      let printerId;
      this.saving = true;
      this.saveError = "";
      try {
        if (create) {
          printerId = Number(textValue(draft.printer_id).trim());
          // leere Felder wie bisher beim Anlegen in der Tabelle nicht mitschicken; Schalter immer
          const device = { printer_id: printerId };
          CREATE_FIELDS.filter((key) => key !== "printer_id").forEach((key) => {
            const v = value(key);
            if (BOOLEAN_FIELDS.indexOf(key) !== -1 || (v !== null && v !== undefined && v !== "")) {
              device[key] = v;
            }
          });
          await this.devicesDataSource.insert(device);
        } else {
          const changes = {};
          this.changedFields.forEach((key) => {
            changes[key] = value(key);
          });
          printerId = row.printer_id;
          await this.devicesDataSource.update(
            { customer_id: row.customer_id, printer_id: row.printer_id, device_id: row.device_id },
            changes
          );
        }
      } catch (err) {
        this.saving = false;
        this.saveError = create ? "Drucker ID bereits vergeben?" : "Änderungen konnten nicht gespeichert werden.";
        return;
      }
      // gespeichert: der Entwurf ist der neue Stand; neu laden (wie refresh-mode="full") und Auswahl wiederherstellen
      this.original = draft;
      const grid = this.grid();
      if (grid) {
        try {
          await grid.refresh();
        } catch (err) {
          // Ladefehler zeigt das Grid selbst
        }
      }
      this.saving = false;
      if (this._isDestroyed || this.dirty) {
        return;
      }
      const fresh = this.findDevice(printerId);
      if (!fresh) {
        if (create) {
          this.closeDetail();
        }
        return;
      }
      this.openEdit(fresh);
      const current = this.grid();
      if (current) {
        const key = current.keyOf(fresh);
        if (current.getRowIndexByKey(key) >= 0) {
          current.option("focusedRowKey", key);
        }
      }
    },

    // ---------------------------------------------------------------- Rückfrage „Änderungen verwerfen?“
    askDiscard(onDiscard, onKeep) {
      const previousKeep = this.pendingKeep;
      this.pendingDiscard = onDiscard;
      this.pendingKeep = onKeep || null;
      if (previousKeep) {
        previousKeep();
      }
      this.discardDialog = true;
    },
    confirmDiscard() {
      const action = this.pendingDiscard;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      this.revertChanges();
      if (action) {
        action();
      }
    },
    keepEditing() {
      const action = this.pendingKeep;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      if (action) {
        action();
      }
    },
    onDiscardDialogInput(open) {
      if (!open) {
        this.keepEditing();
      }
    },

    // ---------------------------------------------------------------- Letzter Standort (Karte im Dialog)
    async showLastLocation(printerId) {
      this.dialog = true;
      this.loading = true;
      this.printer_id = printerId;
      this.lastLocation = { lat: null, lng: null };
      const response = await axios.get(
        `${this.$store.state.baseUrl}gps?customer_id=${this.$store.state.customerId}&printer_id=${printerId}&last=true`
      );
      this.loading = false;
      this.tooltip = `<div>
           Linie: ${response.data.line_id}<br>
           Fahrt: ${response.data.trip_id}<br>
           Drucker: ${response.data.printer_id}<br>
           Fahrer: ${response.data.user_id}<br>
           Geschw.: ${Math.round(response.data.speed)} km/h<br>
           Zeit: ${moment(response.data.time).format("HH:mm:ss")}<br>
           Datum: ${moment(response.data.time).format("L")}<br>
           </div>`;

      this.lastLocation = {
        lat: response.data.latitude,
        lng: response.data.longitude,
      };
    },
  },
};
</script>

<style scoped>
/* Tabelle und Fußleiste als Spalte links vom Detailbereich (wie Benutzer) */
.dev-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Handy: Grid in natürlicher Höhe */
.dev-grid.dev-grid--auto {
  flex: none;
}
.dev-grid ::v-deep .dev-printer {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.dev-grid ::v-deep .dev-time {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
/* Alter des letzten Uploads: Punkt 8 grün (unter 24 Stunden), gelb (bis 7 Tage), grau (älter oder nie) */
.dev-grid ::v-deep .dev-age {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--st-none);
}
.dev-grid ::v-deep .dev-age--fresh {
  background: var(--st-ok);
}
.dev-grid ::v-deep .dev-age--stale {
  background: var(--st-warn);
}
.dev-grid ::v-deep .dev-muted {
  color: var(--ink-3);
}
/* Status: Punkt 8 (aktiv grün gefüllt, inaktiv grauer Ring), Text, Tag „Test“ */
.dev-grid ::v-deep .dev-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.dev-grid ::v-deep .dev-status__dot {
  flex: none;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
}
.dev-grid ::v-deep .dev-status__dot--on {
  background: var(--green-dot);
}
.dev-grid ::v-deep .dev-status__dot--off {
  border: 1.5px solid var(--ink-4);
}
.dev-grid ::v-deep .dev-status__text {
  font-weight: 500;
  color: var(--ink);
}
/* inaktive Geräte blasser */
.dev-grid ::v-deep .dx-data-row.dev-row--off > td,
.dev-grid ::v-deep .dx-data-row.dev-row--off .dev-printer,
.dev-grid ::v-deep .dx-data-row.dev-row--off .dev-status__text {
  color: var(--ink-3);
}
/* Fuß des Detailbereichs wie bei Benutzer */
.dev-foot__text {
  flex: 1 1 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: -4px -16px 0;
  padding: 8px 16px;
  border-bottom: 1px solid var(--line-2);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
.dev-foot__text--error {
  color: var(--red);
}
p.dev-dialog__text {
  margin: 0;
}
.device-map {
  position: relative;
  border-radius: var(--r-card);
  overflow: hidden;
}

/* Kartenmarker des Geräts (vorher global und Teal; Live-Tracking und Fahrplanübersicht definieren .leaflet-icon selbst) */
.device-map ::v-deep .leaflet-icon {
  border-top: 3px solid transparent;
  background-color: var(--d-mark);
  line-height: 100%;
  height: 80%;
  width: 80%;
  position: absolute;
  color: white;
  padding: 5px;
  font-weight: 600;
  border-radius: var(--r-ctl) !important;
}
</style>
